import { useState, useEffect } from 'react';
import { 
  Settings, 
  Save, 
  Power, 
  PowerOff, 
  Mail, 
  Phone, 
  MessageCircle,
  Facebook,
  Youtube,
  Music,
  Link as LinkIcon,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuthContext } from '../contexts/AuthContext';

export default function AdminSettings() {
  const { user } = useAuthContext();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('ระบบกำลังปรับปรุง กลับมาเร็วๆ นี้');
  
  const [contactEmail, setContactEmail] = useState('');
  const [contactLine, setContactLine] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  
  const [socialFacebook, setSocialFacebook] = useState('');
  const [socialYoutube, setSocialYoutube] = useState('');
  const [socialTiktok, setSocialTiktok] = useState('');
  
  const [footerPrivacy, setFooterPrivacy] = useState('/privacy');
  const [footerTerms, setFooterTerms] = useState('/terms');
  const [footerContact, setFooterContact] = useState('/contact');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const docRef = doc(db, 'systemSettings', 'general');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        setMaintenanceEnabled(data.maintenance?.enabled || false);
        setMaintenanceMessage(data.maintenance?.message || 'ระบบกำลังปรับปรุง กลับมาเร็วๆ นี้');
        
        setContactEmail(data.contact?.email || '');
        setContactLine(data.contact?.line || '');
        setContactPhone(data.contact?.phone || '');
        
        setSocialFacebook(data.social?.facebook || '');
        setSocialYoutube(data.social?.youtube || '');
        setSocialTiktok(data.social?.tiktok || '');
        
        setFooterPrivacy(data.footerLinks?.privacy || '/privacy');
        setFooterTerms(data.footerLinks?.terms || '/terms');
        setFooterContact(data.footerLinks?.contact || '/contact');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setErrorMessage('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      const docRef = doc(db, 'systemSettings', 'general');
      await setDoc(docRef, {
        maintenance: {
          enabled: maintenanceEnabled,
          message: maintenanceMessage
        },
        contact: {
          email: contactEmail,
          line: contactLine,
          phone: contactPhone
        },
        social: {
          facebook: socialFacebook,
          youtube: socialYoutube,
          tiktok: socialTiktok
        },
        footerLinks: {
          privacy: footerPrivacy,
          terms: footerTerms,
          contact: footerContact
        },
        updatedAt: new Date(),
        updatedBy: user.email
      });
      
      setSuccessMessage('✅ บันทึกการตั้งค่าสำเร็จ!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setErrorMessage('❌ เกิดข้อผิดพลาดในการบันทึก');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const toggleMaintenance = async () => {
    const newState = !maintenanceEnabled;
    setMaintenanceEnabled(newState);
    
    if (!user) return;
    
    try {
      const docRef = doc(db, 'systemSettings', 'general');
      const docSnap = await getDoc(docRef);
      const existingData = docSnap.exists() ? docSnap.data() : {};
      
      await setDoc(docRef, {
        ...existingData,
        maintenance: {
          enabled: newState,
          message: maintenanceMessage
        },
        updatedAt: new Date(),
        updatedBy: user.email
      });
      
      setSuccessMessage(newState ? '🔴 เปิดโหมดบำรุงรักษาแล้ว!' : '🟢 ปิดโหมดบำรุงรักษาแล้ว!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error toggling maintenance:', error);
      setMaintenanceEnabled(!newState);
      setErrorMessage('เกิดข้อผิดพลาดในการเปลี่ยนสถานะ');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลดการตั้งค่า...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">ตั้งค่าระบบ</h2>
            <p className="text-sm text-gray-600">จัดการการตั้งค่าทั่วไปของระบบ</p>
          </div>
        </div>
      </div>

      {successMessage && (
        <div className="bg-green-50 border-2 border-green-500 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-green-800 font-medium">{successMessage}</p>
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800 font-medium">{errorMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg p-6 space-y-8">
        {/* Maintenance Mode */}
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-4 border-b-2 border-gray-100">
            <div className="flex items-center gap-3">
              {maintenanceEnabled ? (
                <PowerOff className="w-6 h-6 text-red-600" />
              ) : (
                <Power className="w-6 h-6 text-green-600" />
              )}
              <div>
                <h3 className="text-lg font-bold text-gray-900">โหมดบำรุงรักษา</h3>
                <p className="text-sm text-gray-600">เปิด/ปิดเว็บไซต์ชั่วคราว</p>
              </div>
            </div>
            <button
              onClick={toggleMaintenance}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                maintenanceEnabled ? 'bg-red-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  maintenanceEnabled ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              ข้อความแจ้งผู้ใช้
            </label>
            <textarea
              value={maintenanceMessage}
              onChange={(e) => setMaintenanceMessage(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
              placeholder="ระบบกำลังปรับปรุง กลับมาเร็วๆ นี้"
            />
          </div>
        </div>

        {/* Contact Info */}
        <div className="space-y-4 pt-6 border-t-2 border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-bold text-gray-900">ข้อมูลติดต่อ</h3>
              <p className="text-sm text-gray-600">แสดงใน Footer ของเว็บไซต์</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              อีเมล
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                placeholder="support@คอร์สดี.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              LINE ID
            </label>
            <div className="relative">
              <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={contactLine}
                onChange={(e) => setContactLine(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                placeholder="@coursedee"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              เบอร์โทรศัพท์
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                placeholder="081-234-5678"
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="space-y-4 pt-6 border-t-2 border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <Facebook className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-bold text-gray-900">โซเชียลมีเดีย</h3>
              <p className="text-sm text-gray-600">ลิงก์ไปยังช่องทางโซเชียล</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Facebook
            </label>
            <div className="relative">
              <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={socialFacebook}
                onChange={(e) => setSocialFacebook(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                placeholder="https://facebook.com/coursedee"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              YouTube
            </label>
            <div className="relative">
              <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={socialYoutube}
                onChange={(e) => setSocialYoutube(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                placeholder="https://youtube.com/@coursedee"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              TikTok
            </label>
            <div className="relative">
              <Music className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={socialTiktok}
                onChange={(e) => setSocialTiktok(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                placeholder="https://tiktok.com/@coursedee"
              />
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="space-y-4 pt-6 border-t-2 border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <LinkIcon className="w-6 h-6 text-purple-600" />
            <div>
              <h3 className="text-lg font-bold text-gray-900">ลิงก์เมนู Footer</h3>
              <p className="text-sm text-gray-600">URL สำหรับเมนูใน Footer</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              นโยบายความเป็นส่วนตัว
            </label>
            <input
              type="text"
              value={footerPrivacy}
              onChange={(e) => setFooterPrivacy(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
              placeholder="/privacy"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              เงื่อนไขการใช้งาน
            </label>
            <input
              type="text"
              value={footerTerms}
              onChange={(e) => setFooterTerms(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
              placeholder="/terms"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              ติดต่อเรา
            </label>
            <input
              type="text"
              value={footerContact}
              onChange={(e) => setFooterContact(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
              placeholder="/contact"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-6 border-t-2 border-gray-100">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            <span>{saving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
